import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-base font-semibold ring-offset-background transition-all duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:size-5 [&_svg]:shrink-0 [&_svg]:transition-transform [&_svg]:duration-300",
  {
    variants: {
      variant: {
        // Primary CTA – Cyan #23c0dd filled, white text, darker hover
        default:
          "bg-[#23c0dd] text-white hover:bg-[#1a9bb5] active:bg-[#17899f] shadow-md shadow-[#23c0dd]/20 hover:shadow-lg hover:shadow-[#23c0dd]/30 hover:-translate-y-0.5 active:translate-y-0",
        hero:
          "bg-[#23c0dd] text-white hover:bg-[#1a9bb5] active:bg-[#17899f] shadow-lg shadow-[#23c0dd]/25 hover:shadow-xl hover:shadow-[#23c0dd]/40 hover:-translate-y-0.5 active:translate-y-0 font-bold",
        // Pill primary — Cyan filled, subtle scale on hover, min 44px tap target
        "pill-primary":
          "rounded-pill bg-[#23C0DD] text-white font-bold min-h-11 px-7 shadow-md shadow-[#23C0DD]/25 transition-all duration-200 ease-out hover:bg-[#1BADC8] hover:scale-[1.02] active:scale-100 focus-visible:ring-2 focus-visible:ring-[#23C0DD] focus-visible:ring-offset-2",
        // Pill ghost — transparent, 2px white border, for dark/gradient backgrounds
        "pill-ghost":
          "rounded-pill bg-transparent text-white font-bold min-h-11 px-7 border-2 border-white transition-all duration-200 ease-out hover:bg-white/10 hover:border-white focus-visible:ring-2 focus-visible:ring-[#23C0DD] focus-visible:ring-offset-2",
        // Pill ghost dark — 2px Acai border, Acai text, for use on white backgrounds
        "pill-ghost-dark":
          "rounded-pill bg-transparent text-[#44235F] font-bold min-h-11 px-7 border-2 border-[#44235F] transition-all duration-200 ease-out hover:bg-[#44235F]/5 focus-visible:ring-2 focus-visible:ring-[#23C0DD] focus-visible:ring-offset-2",
        // Outline – Cyan border, cyan text
        outline:
          "border-2 border-[#23c0dd] text-[#23c0dd] bg-transparent hover:bg-[#23c0dd]/10 active:bg-[#23c0dd]/15",
        // Secondary – soft cyan panel
        secondary:
          "bg-[#23c0dd]/10 text-[#23c0dd] hover:bg-[#23c0dd]/20",
        // Ghost
        ghost:
          "text-[#44235F] hover:text-[#23c0dd] hover:bg-[#23c0dd]/5 active:bg-[#23c0dd]/10",
        // Link
        link:
          "text-[#44235F] underline-offset-4 hover:underline hover:text-[#23c0dd]",
        // Destructive
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        // ComplyHub canonical primary — cyan, always fully rounded
        chPrimary:
          "bg-ch-cyan text-white rounded-full font-carlito font-semibold hover:bg-ch-cyan/90 shadow-md shadow-ch-cyan/20 hover:shadow-lg hover:shadow-ch-cyan/30 transition-all duration-200",
        // ComplyHub canonical outline on light backgrounds
        chOutline:
          "border-2 border-ch-purple text-ch-purple bg-transparent rounded-full font-carlito font-semibold hover:bg-ch-purple hover:text-white transition-all duration-200",
        // ComplyHub canonical outline on dark/gradient backgrounds
        chOutlineOnDark:
          "border-2 border-white text-white bg-transparent rounded-full font-carlito font-semibold hover:bg-white/10 transition-all duration-200",
      },
      size: {
        default: "h-10 px-6 py-2",       /* 40 px */
        sm: "h-10 rounded-lg px-4 text-sm", /* 40 px */
        lg: "h-12 rounded-lg px-8 text-base", /* 48 px */
        xl: "h-12 rounded-lg px-10 text-lg",  /* 48 px */
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
